import React, { useEffect, useRef } from "react";

// URL oficial do script da Cloudflare Turnstile.
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

// loadScript injeta o script da Cloudflare uma única vez e devolve uma
// Promise que resolve quando `window.turnstile` está disponível.
function loadScript() {
    return new Promise((resolve, reject) => {
        if (typeof window === "undefined") return reject(new Error("no-window"));
        if (window.turnstile) return resolve(window.turnstile);

        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            existing.addEventListener("load", () => resolve(window.turnstile));
            existing.addEventListener("error", () => reject(new Error("turnstile-script-error")));
            return;
        }

        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.src = SCRIPT_URL;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve(window.turnstile);
        s.onerror = () => reject(new Error("turnstile-script-error"));
        document.head.appendChild(s);
    });
}

/**
 * Componente Turnstile — encapsula o widget oficial da Cloudflare.
 *
 * Props:
 *   - siteKey: string (obrigatório)
 *   - onVerify(token): chamado quando o usuário resolve o desafio
 *   - onExpire(): chamado quando o token expira
 *   - onError(err): chamado em erros do widget
 *   - theme: "light" | "dark" | "auto" (default "dark")
 *   - resetKey: qualquer valor; quando muda, força reset do widget
 */
export default function Turnstile({
    siteKey,
    onVerify,
    onExpire,
    onError,
    theme = "dark",
    resetKey,
}) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onVerifyRef = useRef(onVerify);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);

    // Mantém refs sempre atualizadas para os callbacks
    useEffect(() => { onVerifyRef.current = onVerify; }, [onVerify]);
    useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    // Monta o widget uma vez
    useEffect(() => {
        let cancelled = false;
        if (!siteKey) return undefined;

        loadScript()
            .then((turnstile) => {
                if (cancelled || !containerRef.current || !turnstile) return;
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    theme,
                    callback: (token) => onVerifyRef.current && onVerifyRef.current(token),
                    "expired-callback": () => onExpireRef.current && onExpireRef.current(),
                    "error-callback": (err) => onErrorRef.current && onErrorRef.current(err),
                    "timeout-callback": () => onExpireRef.current && onExpireRef.current(),
                });
            })
            .catch((err) => {
                if (onErrorRef.current) onErrorRef.current(err);
            });

        return () => {
            cancelled = true;
            try {
                if (window.turnstile && widgetIdRef.current !== null) {
                    window.turnstile.remove(widgetIdRef.current);
                }
            } catch (_) { /* silencioso */ }
            widgetIdRef.current = null;
        };
        // siteKey/theme não devem mudar em tempo de execução; se mudarem,
        // remontagem completa do componente.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey, theme]);

    // Quando o pai pede reset (resetKey muda), reseta o widget
    useEffect(() => {
        if (resetKey === undefined) return;
        try {
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
            }
        } catch (_) { /* silencioso */ }
    }, [resetKey]);

    if (!siteKey) {
        return (
            <div className="text-xs text-amber-400">
                ⚠️ Captcha não configurado (REACT_APP_TURNSTILE_SITE_KEY ausente).
            </div>
        );
    }

    return <div ref={containerRef} className="flex justify-start" />;
}
